import { apiRequest } from '../../../shared/api';
import {
  Hazard,
  HazardCategory,
  HazardDashboardStats,
  HazardStatus,
  HazardSeverity,
  HazardLikelihood,
  HazardCause,
  HazardConsequence,
  HazardControl,
  HazardSource,
  HazardReview,
} from './types';

export interface HazardListResponse {
  items: Hazard[];
  total: number;
}

export const hazardService = {
  // Categories
  getCategories: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<HazardCategory[]>(`/hazards/categories?organizationId=${organizationId}`),

  createCategory: (data: {
    organizationId?: string;
    code: string;
    name: string;
    parentId?: string;
    description?: string;
  }) =>
    apiRequest<HazardCategory>('/hazards/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Dashboard Stats
  getDashboardStats: (organizationId: string = 'ORG-XYZ') =>
    apiRequest<HazardDashboardStats>(`/hazards/analytics/dashboard?organizationId=${organizationId}`),

  // Hazards List
  getHazards: (params: {
    organizationId?: string;
    status?: HazardStatus | HazardStatus[];
    categoryId?: string;
    severityEstimate?: HazardSeverity;
    likelihoodEstimate?: HazardLikelihood;
    ownerId?: string;
    searchTerm?: string;
    limit?: number;
    offset?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.organizationId) query.append('organizationId', params.organizationId);
    if (params.status) {
      if (Array.isArray(params.status)) {
        params.status.forEach((s) => query.append('status', s));
      } else {
        query.append('status', params.status);
      }
    }
    if (params.categoryId) query.append('categoryId', params.categoryId);
    if (params.severityEstimate) query.append('severityEstimate', params.severityEstimate);
    if (params.likelihoodEstimate) query.append('likelihoodEstimate', params.likelihoodEstimate);
    if (params.ownerId) query.append('ownerId', params.ownerId);
    if (params.searchTerm) query.append('searchTerm', params.searchTerm);
    if (params.limit) query.append('limit', params.limit.toString());
    if (params.offset) query.append('offset', params.offset.toString());

    return apiRequest<HazardListResponse>(`/hazards?${query.toString()}`);
  },

  // Get Detail
  getHazardById: (id: string) => apiRequest<Hazard>(`/hazards/${id}`),

  // Create
  createHazard: (data: Partial<Hazard>) =>
    apiRequest<Hazard>('/hazards', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // Update
  updateHazard: (id: string, data: Partial<Hazard>) =>
    apiRequest<Hazard>(`/hazards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  // Transition Status
  transitionStatus: (id: string, toStatus: HazardStatus, reason: string, changedBy?: string) =>
    apiRequest<Hazard>(`/hazards/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify({ toStatus, reason, changedBy }),
    }),

  // Causes
  addCause: (id: string, causeText: string, causeType: string) =>
    apiRequest<HazardCause>(`/hazards/${id}/causes`, {
      method: 'POST',
      body: JSON.stringify({ causeText, causeType }),
    }),

  removeCause: (id: string, causeId: string) =>
    apiRequest<{ success: boolean }>(`/hazards/${id}/causes/${causeId}`, {
      method: 'DELETE',
    }),

  // Consequences
  addConsequence: (id: string, consequenceText: string, severityLevel: string) =>
    apiRequest<HazardConsequence>(`/hazards/${id}/consequences`, {
      method: 'POST',
      body: JSON.stringify({ consequenceText, severityLevel }),
    }),

  removeConsequence: (id: string, consequenceId: string) =>
    apiRequest<{ success: boolean }>(`/hazards/${id}/consequences/${consequenceId}`, {
      method: 'DELETE',
    }),

  // Controls
  addControl: (
    id: string,
    controlTitle: string,
    controlType: string,
    description: string,
    effectiveness?: string,
    responsibleDepartment?: string
  ) =>
    apiRequest<HazardControl>(`/hazards/${id}/controls`, {
      method: 'POST',
      body: JSON.stringify({
        controlTitle,
        controlType,
        description,
        effectiveness,
        responsibleDepartment,
      }),
    }),

  updateControlStatus: (id: string, controlId: string, isActive: boolean, effectiveness?: string) =>
    apiRequest<{ success: boolean }>(`/hazards/${id}/controls/${controlId}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive, effectiveness }),
    }),

  removeControl: (id: string, controlId: string) =>
    apiRequest<{ success: boolean }>(`/hazards/${id}/controls/${controlId}`, {
      method: 'DELETE',
    }),

  // Sources Junction
  linkSource: (id: string, sourceType: string, sourceReferenceId: string, description?: string) =>
    apiRequest<HazardSource>(`/hazards/${id}/sources`, {
      method: 'POST',
      body: JSON.stringify({ sourceType, sourceReferenceId, description }),
    }),

  unlinkSource: (id: string, sourceId: string) =>
    apiRequest<{ success: boolean }>(`/hazards/${id}/sources/${sourceId}`, {
      method: 'DELETE',
    }),

  // Periodic Reviews
  recordReview: (
    id: string,
    reviewerId: string,
    reviewerName: string,
    verdict: string,
    comments: string,
    customNextReviewDays?: number
  ) =>
    apiRequest<HazardReview>(`/hazards/${id}/reviews`, {
      method: 'POST',
      body: JSON.stringify({ reviewerId, reviewerName, verdict, comments, customNextReviewDays }),
    }),

  // Comments
  addComment: (id: string, commentText: string, userId?: string, userName?: string) =>
    apiRequest<any>(`/hazards/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ commentText, userId, userName }),
    }),

  // Attachments
  addAttachment: (
    id: string,
    fileName: string,
    fileType: string,
    fileSize: number,
    storagePath: string,
    uploadedBy?: string
  ) =>
    apiRequest<any>(`/hazards/${id}/attachments`, {
      method: 'POST',
      body: JSON.stringify({ fileName, fileType, fileSize, storagePath, uploadedBy }),
    }),
};
